import React, { Component } from 'react';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import {
  Button, Intent
} from "@blueprintjs/core";
import QuoteHeader from 'components/Quote/Header';
import QuoteCustomerDetails from 'components/Quote/CustomerDetails';
import QuoteDescription from 'components/Quote/Description';
import QuoteLabourLineItems from 'components/Quote/LabourLineItems';
import QuoteMaterialLineItems from 'components/Quote/MaterialLineItems';
import QuoteCustomLineItems from 'components/Quote/CustomLineItems';
import QuoteEquipments from 'components/Quote/Equipments';
import ProposalCoverEditor from 'components/Quote/ProposalCoverEditor';
import QuoteImages from 'components/Quote/Images';
import {
  ColumnContainer,
  Column,
  Card
} from 'components/Styles';

import Toast from 'helpers/toaster';
import _ from 'lodash';
import QuoteSignatureSection from 'components/Quote/SignatureSection';
import { currencyFormattedAmount, currency } from 'helpers/currency';
import UpdateClientModal from './UpdateClientModal';

@inject('store', 'quoteStore', 'viewStore') @observer
class QuoteShow extends Component {

  calculateSubTotal() {
    const {
      quoteStore: {
        quote: { object }
      }
    } = this.props;

    let total = 0.0;

    const calculateItemTotal = (items) => {
      return _.sumBy(items, (item) => {
        return (item.rate_per_unit || 0) * (item.markup || 0) * (item.quantity || 0);
      });
    };

    if (object.attributes.labour.length > 0) {
      total += calculateItemTotal(object.attributes.labour);
    }

    if (object.attributes.materials.length > 0) {
      total += calculateItemTotal(object.attributes.materials);
    }

    if (object.attributes.custom_line_items.length > 0) {
      total += calculateItemTotal(object.attributes.custom_line_items);
    }

    return currencyFormattedAmount(total);
  }

  calculateTax() {
    const {
      quoteStore: {
        quote: { object }
      }
    } = this.props;

    let total = 0.0;

    const calculateTotalTax = (items) => {
      return _.sumBy(items, (item) => {
        const itemTotal = (item.rate_per_unit || 0) * (item.markup || 0) * (item.quantity || 0);
        return _.sumBy(item.tax_line_items, (taxLineItem) => {
          return (taxLineItem.percentage_value / 100) * itemTotal;
        });
      });
    };

    if (object.attributes.labour.length > 0) {
      total += calculateTotalTax(object.attributes.labour);
    }

    if (object.attributes.materials.length > 0) {
      total += calculateTotalTax(object.attributes.materials);
    }

    if (object.attributes.custom_line_items.length > 0) {
      total += calculateTotalTax(object.attributes.custom_line_items);
    }

    return currencyFormattedAmount(total);
  }

  calculateGrandTotal() {
    const {
      quoteStore: {
        quote: { object }
      }
    } = this.props;

    let total = 0.0;

    // Helper function to calculate total with tax
    const calculateTotalWithTax = (items) => {
      return _.sumBy(items, (item) => {
        const itemTotal = (item.rate_per_unit || 0) * (item.markup || 0) * (item.quantity || 0);
        const taxTotal = _.sumBy(item.tax_line_items, (taxLineItem) => {
          return (taxLineItem.percentage_value / 100) * itemTotal;
        });
        return itemTotal + taxTotal;
      });
    };

    // Calculate total for labour
    if (object.attributes.labour.length > 0) {
      total += calculateTotalWithTax(object.attributes.labour);
    }

    // Calculate total for materials
    if (object.attributes.materials.length > 0) {
      total += calculateTotalWithTax(object.attributes.materials);
    }

    // Calculate total for custom line items
    if (object.attributes.custom_line_items.length > 0) {
      total += calculateTotalWithTax(object.attributes.custom_line_items);
    }

    return currencyFormattedAmount(total);
  }
  calculateGrossMargin() {
    const {
      quoteStore: {
        quote: { object }
      }
    } = this.props;

    let total = 0;
    let total_price = 0;

    // Calculate for materials
    if (object.attributes.materials.length > 0) {
      total += _.sumBy(object.attributes.materials, (material) => material.total);
      total_price += _.sumBy(object.attributes.materials, (material) => material.quantity * material.rate_per_unit);
    }

    // Calculate for labour
    if (object.attributes.labour.length > 0) {
      total += _.sumBy(object.attributes.labour, (labour) => labour.total);
      total_price += _.sumBy(object.attributes.labour, (labour) => labour.quantity * labour.rate_per_unit);
    }

    // Calculate for custom line items
    if (object.attributes.custom_line_items.length > 0) {
      total += _.sumBy(object.attributes.custom_line_items, (custom) => custom.total);
      total_price += _.sumBy(object.attributes.custom_line_items, (custom) => custom.quantity * custom.rate_per_unit);
    }

    return currencyFormattedAmount(total - total_price);
  }

  calculateGrossMarkup() {
    const {
      quoteStore: {
        quote: { object }
      }
    } = this.props;

    let total = 0;
    let total_price = 0;

    // Calculate for materials
    if (object.attributes.materials.length > 0) {
      total += _.sumBy(object.attributes.materials, (material) => material.total);
      total_price += _.sumBy(object.attributes.materials, (material) => material.quantity * material.rate_per_unit);
    }

    // Calculate for labour
    if (object.attributes.labour.length > 0) {
      total += _.sumBy(object.attributes.labour, (labour) => labour.total);
      total_price += _.sumBy(object.attributes.labour, (labour) => labour.quantity * labour.rate_per_unit);
    }

    // Calculate for custom line items
    if (object.attributes.custom_line_items.length > 0) {
      total += _.sumBy(object.attributes.custom_line_items, (custom) => custom.total);
      total_price += _.sumBy(object.attributes.custom_line_items, (custom) => custom.quantity * custom.rate_per_unit);
    }

    if (total > 0) {
      let gross_margin = total - total_price;
      return Math.round((gross_margin / total_price) * 100) + '%';
    } else {
      return "0%";
    }
  }

  updateQuote() {
    this.props.quoteStore.update()
      .then(this.onUpdateSuccessfully.bind(this))
      .catch(this.onFailUpdate.bind(this))

  }

  onUpdateSuccessfully(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Updated successfully!', intent: Intent.SUCCESS })
  }

  onFailUpdate(error) {
    Toast.show({ icon: 'warning-sign', message: 'Unable to update!', intent: Intent.DANGER })
  }

  render() {
    const {
      quoteStore: {
        quote: {
          object,
          isLoading
        }
      },
      store
    } = this.props;
    return (
      <div className="pdf-view-layout">
        <ColumnContainer>
          <Column>
            <Card>
              {/* Header: Logo + Tenant Details */}
              <QuoteHeader />

              {/* Customer Details */}
              <QuoteCustomerDetails selectedTechnician={[{ id: object.attributes.created_by.id, label: object.attributes.created_by.full_name, value: object.attributes.created_by.id, selected: true }]} selectedJob={[{ id: object.attributes.job.id, label: object.attributes.job.number, value: object.attributes.job.id, selected: true }]} />

              {/* editor */}
              <ProposalCoverEditor />

              {/* Equipments */}
              <QuoteEquipments />

              {/* Work Report Images */}
              <QuoteImages />

              {/* Material Line Items */}
              <QuoteMaterialLineItems />
              <div style={{ marginTop: 40 }}></div>

              {/* Labor Line Items */}
              <QuoteLabourLineItems />
              <div style={{ marginTop: 40 }}></div>

              {/* Custom Line Items */}
              <QuoteCustomLineItems />

              <div className='grandTotalEstimateWrapper'>
                <div className='totalDetails'>
                  <div className='totalDetailsRow'> 
                    <div className='totalDetailsRowLabel'>Sub Total:</div>
                    <div className='totalDetailsRowValue'>{this.calculateSubTotal()}</div>
                  </div>
                  <div className='totalDetailsRow'> 
                    <div className='totalDetailsRowLabel'>Tax:</div>
                    <div className='totalDetailsRowValue'>{this.calculateTax()}</div>
                  </div>
                  <div className='totalDetailsRow'> 
                    <div className='totalDetailsRowLabel' style={{fontSize: '18px', fontWeight: '600'}}>Grand Total:</div>
                    <div className='totalDetailsRowValue' style={{fontSize: '18px', fontWeight: '600'}}>{this.calculateGrandTotal()}</div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 45 }}></div>

              {/* Signatures */}
              <QuoteSignatureSection />

              {/* Reason For Call/Description */}
              <QuoteDescription />


              

              <div style={{ marginTop: 45 }}></div>

              <div style={{ marginTop: 0, clear: 'both' }}></div>
              <br />
            </Card>
            <div className="invoice-save grandTotalWrapper">
              <div className="lineitem_grand_total grand-total">
                <div className='totalDetails'>
                  <span>Gross Markup: {this.calculateGrossMarkup()}</span>
                  <span>Gross Margin: {this.calculateGrossMargin()}</span>
                </div>
                <h5>Grand Total ({currency()}): {this.calculateGrandTotal()}</h5>
              </div>
              <Button
                large={true}
                loading={isLoading}
                onClick={this.updateQuote.bind(this)}
              >
                Save
              </Button>
            </div>
          </Column>
        </ColumnContainer>
        <UpdateClientModal
          isOpen={store.isOpenUpdateClientDialog}
          onClose={store.handleCloseUpdateClientDailog.bind(store)}
        />
      </div>
    )
  }
}

export default QuoteShow;